import { NotificationSettings } from '../types';

export enum NotificationType {
  TURN_BASED = 'turn_based',
  GAME_INVITE = 'game_invite',
  GAME_RESULT = 'game_result',
  SUCCESS = 'success',
  ERROR = 'error',
  WARNING = 'warning',
  INFO = 'info'
}

export interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  timestamp: Date;
  duration?: number; // Auto-dismiss after this many milliseconds
  persistent?: boolean; // Don't auto-dismiss
  actionLabel?: string;
  onAction?: () => void;
  gameId?: string;
  playerId?: string;
}

export interface NotificationState {
  notifications: Notification[];
  settings: NotificationSettings;
}

type NotificationListener = (notifications: Notification[]) => void;

export class NotificationService {
  private static instance: NotificationService;
  private notifications: Notification[] = [];
  private settings: NotificationSettings = {
    turnBased: true,
    gameInvites: true,
    gameResults: true
  };
  private listeners: NotificationListener[] = [];
  private notificationCounter = 0;

  private constructor() {
    this.loadSettings();
    this.setupServiceWorkerNotifications();
  }

  static getInstance(): NotificationService {
    if (!NotificationService.instance) {
      NotificationService.instance = new NotificationService();
    }
    return NotificationService.instance;
  }

  /**
   * Add a notification to the queue
   */
  addNotification(notification: Omit<Notification, 'id' | 'timestamp'>): string {
    const id = `notification-${++this.notificationCounter}-${Date.now()}`;
    const newNotification: Notification = {
      ...notification,
      id,
      timestamp: new Date(),
      duration: notification.duration ?? (notification.persistent ? undefined : 5000)
    };

    // Check if this type of notification is enabled
    if (!this.isNotificationTypeEnabled(notification.type)) {
      return id;
    }

    this.notifications.unshift(newNotification);
    this.notifyListeners();

    // Auto-dismiss if not persistent and has duration
    if (!notification.persistent && newNotification.duration) {
      setTimeout(() => {
        this.removeNotification(id);
      }, newNotification.duration);
    }

    // Try to show browser notification for important types
    if (this.shouldShowBrowserNotification(notification.type)) {
      this.showBrowserNotification(newNotification);
    }

    return id;
  }

  /**
   * Remove a notification by ID
   */
  removeNotification(id: string): void {
    const index = this.notifications.findIndex(n => n.id === id);
    if (index !== -1) {
      this.notifications.splice(index, 1);
      this.notifyListeners();
    }
  }

  /**
   * Clear all notifications
   */
  clearAll(): void {
    this.notifications = [];
    this.notifyListeners();
  }

  /**
   * Clear notifications of a specific type
   */
  clearByType(type: NotificationType): void {
    this.notifications = this.notifications.filter(n => n.type !== type);
    this.notifyListeners();
  }

  /**
   * Get all current notifications
   */
  getNotifications(): Notification[] {
    return [...this.notifications];
  }

  /**
   * Subscribe to notification changes
   */
  subscribe(listener: NotificationListener): () => void {
    this.listeners.push(listener);
    return () => {
      const index = this.listeners.indexOf(listener);
      if (index !== -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  /**
   * Update notification settings
   */
  updateSettings(settings: Partial<NotificationSettings>): void {
    this.settings = { ...this.settings, ...settings };
    this.saveSettings();
  }

  /**
   * Get current notification settings
   */
  getSettings(): NotificationSettings {
    return { ...this.settings };
  }

  /**
   * Send turn-based game notification
   */
  sendTurnNotification(gameId: string, playerName: string): void {
    this.addNotification({
      type: NotificationType.TURN_BASED,
      title: 'Your Turn!',
      message: `It's your turn in the game with ${playerName}`,
      persistent: true,
      actionLabel: 'Play Now',
      onAction: () => {
        // Navigate to game - this would be handled by the app
        window.dispatchEvent(new CustomEvent('navigate-to-game', { detail: { gameId } }));
      },
      gameId
    });
  }

  /**
   * Send game invite notification
   */
  sendGameInviteNotification(fromPlayer: string, gameId: string): void {
    this.addNotification({
      type: NotificationType.GAME_INVITE,
      title: 'Game Invitation',
      message: `${fromPlayer} invited you to play Tic-Tac-Toe`,
      persistent: true,
      actionLabel: 'Join Game',
      onAction: () => {
        window.dispatchEvent(new CustomEvent('join-game', { detail: { gameId } }));
      },
      gameId
    });
  }

  /**
   * Send game result notification
   */
  sendGameResultNotification(result: 'win' | 'loss' | 'draw', opponent?: string): void {
    const messages = {
      win: opponent ? `You won against ${opponent}!` : 'You won the game!',
      loss: opponent ? `You lost to ${opponent}` : 'You lost the game',
      draw: opponent ? `Draw with ${opponent}` : 'Game ended in a draw'
    };

    this.addNotification({
      type: NotificationType.GAME_RESULT,
      title: 'Game Complete',
      message: messages[result],
      duration: 8000
    });
  }

  /**
   * Send success notification
   */
  sendSuccessNotification(message: string, duration?: number): void {
    this.addNotification({
      type: NotificationType.SUCCESS,
      title: 'Success',
      message,
      duration
    });
  }

  /**
   * Send error notification
   */
  sendErrorNotification(message: string, persistent = false): void {
    this.addNotification({
      type: NotificationType.ERROR,
      title: 'Error',
      message,
      persistent,
      duration: persistent ? undefined : 8000
    });
  }

  /**
   * Send warning notification
   */
  sendWarningNotification(message: string, duration?: number): void {
    this.addNotification({
      type: NotificationType.WARNING,
      title: 'Warning',
      message,
      duration: duration ?? 6000
    });
  }

  /**
   * Send info notification
   */
  sendInfoNotification(message: string, duration?: number): void {
    this.addNotification({
      type: NotificationType.INFO,
      title: 'Info',
      message,
      duration
    });
  }

  private isNotificationTypeEnabled(type: NotificationType): boolean {
    switch (type) {
      case NotificationType.TURN_BASED:
        return this.settings.turnBased;
      case NotificationType.GAME_INVITE:
        return this.settings.gameInvites;
      case NotificationType.GAME_RESULT:
        return this.settings.gameResults;
      default:
        return true; // Always show system notifications
    }
  }

  private shouldShowBrowserNotification(type: NotificationType): boolean {
    return type === NotificationType.TURN_BASED || type === NotificationType.GAME_INVITE;
  }

  private async showBrowserNotification(notification: Notification): Promise<void> {
    if (!('Notification' in window)) {
      return;
    }

    if (Notification.permission === 'granted') {
      try {
        const browserNotification = new Notification(notification.title, {
          body: notification.message,
          icon: '/icon-192.png',
          badge: '/icon-192.png',
          tag: notification.gameId || notification.type,
          requireInteraction: notification.persistent,
          actions: notification.actionLabel ? [{
            action: 'open',
            title: notification.actionLabel
          }] : undefined
        });

        browserNotification.onclick = () => {
          window.focus();
          if (notification.onAction) {
            notification.onAction();
          }
          browserNotification.close();
        };

        // Auto-close browser notification after duration
        if (notification.duration) {
          setTimeout(() => {
            browserNotification.close();
          }, notification.duration);
        }
      } catch (error) {
        console.warn('Failed to show browser notification:', error);
      }
    } else if (Notification.permission === 'default') {
      // Request permission for future notifications
      Notification.requestPermission();
    }
  }

  private setupServiceWorkerNotifications(): void {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data && event.data.type === 'NOTIFICATION_CLICK') {
          const { notificationId, gameId } = event.data;
          if (gameId) {
            window.dispatchEvent(new CustomEvent('navigate-to-game', { detail: { gameId } }));
          }
        }
      });
    }
  }

  private notifyListeners(): void {
    this.listeners.forEach(listener => {
      try {
        listener([...this.notifications]);
      } catch (error) {
        console.error('Error notifying notification listener:', error);
      }
    });
  }

  private loadSettings(): void {
    try {
      const stored = localStorage.getItem('notification-settings');
      if (stored) {
        const parsed = JSON.parse(stored);
        this.settings = { ...this.settings, ...parsed };
      }
    } catch (error) {
      console.warn('Failed to load notification settings:', error);
    }
  }

  private saveSettings(): void {
    try {
      localStorage.setItem('notification-settings', JSON.stringify(this.settings));
    } catch (error) {
      console.warn('Failed to save notification settings:', error);
    }
  }
}

// Export singleton instance
export const notificationService = NotificationService.getInstance();